/* eslint-disable no-nested-ternary */
import * as amplitude from '@amplitude/analytics-browser';
import { Identify } from '@amplitude/analytics-browser';

import { config as configInitial } from './events';
import isUndefined from './isUndefined';
import isString from './isString';
import isObject from './isObject';
import get from './get';

const getValues = (fieldNames) => {
  const values = configInitial;

  return isUndefined(fieldNames)
    ? values
    : isString(fieldNames)
    ? get(values, fieldNames)
    : fieldNames.map((name) => get(values, name));
};

const getData = (fieldNames) => {
  const values = configInitial;
  const rexKey = /[,[\].]+?/;

  if (isString(fieldNames)) {
    const key = fieldNames?.split(rexKey)[0]?.replace('@@', '');
    return {
      [key]: get(values, fieldNames),
    };
  }

  if (isObject(fieldNames)) {
    const dataObject = fieldNames.reduce((a, b) => {
      const key = b?.split(rexKey)[0]?.replace('@@', '');
      return { ...a, [key]: get(values, b) };
    }, {});

    return dataObject;
  }

  return {};
};

/**
 * Sets user properties in Amplitude and optionally sets user ID
 *
 * This function handles user identification and property assignment in Amplitude.
 * If a store_id is provided in the properties object, it will be set as the Amplitude user ID.
 * All properties are then set as user properties using the Identify API.
 *
 * @param {Record<string, any>} properties - User properties to set in Amplitude (accepts any key-value pairs)
 * @param {string} [properties.store_id] - If provided, will be set as Amplitude user ID
 * @param {string} [properties.email] - User email
 * @param {string} [properties.store_name] - Store name
 * @param {string} [properties.organization_id] - Organization ID
 * @param {string} [properties.organization_name] - Organization name
 * @returns {void}
 *
 * @example
 * setUserPropertyRequest({ store_id: '12345', role: 'admin', plan: 'premium' });
 *
 * @example
 * setUserPropertyRequest({
 *   email: 'user@example.com',
 *   store_name: 'My Store',
 *   store_id: '12345',
 *   organization_id: 'org_789',
 *   organization_name: 'My Org'
 * });
 */
export const setUserPropertyRequest = (properties) => {
  if (properties.store_id) {
    amplitude.setUserId(properties.store_id);
  }
  const identifyEvent = new Identify();
  Object.entries(properties).forEach(([key, value]) => {
    identifyEvent.set(key, value);
  });
  amplitude.identify(identifyEvent);
};

/**
 * Logs an event to Amplitude with transformed data and optional extras
 *
 * This function processes event names and data through helper functions (getValues/getData)
 * that transform config-based event keys (with @ and @@ prefixes) into actual values
 * before sending to Amplitude's track API.
 *
 * @param {string|Array} event - Event name key from config (e.g., '@event.$reports.viewReport')
 * @param {string|Array|Object} [data] - Event data keys with @@ prefixes (e.g., '@@section.sales')
 * @param {Object} [extras] - Additional event properties to merge
 * @returns {void}
 *
 * @example
 * logEventRequest('@event.$reports.viewReport', '@@section.sales');
 *
 * @example
 * logEventRequest('@event.$login.login', null, { login_method: 'email' });
 *
 * @example
 * logEventRequest(
 *   '@event.$menuMaker.menuMaker',
 *   ['@@type.edit', '@@item.product'],
 *   { product_id: '123' }
 * );
 */
export const logEventRequest = (event, data, extras) => {
  amplitude.track(getValues(event), { ...getData(data), ...extras });
};
